import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {UserVacationPayload} from '@api/user-api/user-api-request';

dayjs.extend(utc);
dayjs.extend(timezone);

export type ActiveVacation = UserVacationPayload & {
  id?: string;
};

const vacationCandidate = (response: any) => {
  const candidates = [
    response?.data?.vacation,
    response?.data,
    response?.vacation,
    response?.result,
    response,
  ];

  return candidates.find(
    (candidate) => candidate?.startDate && candidate?.endDate,
  );
};

export const normalizeVacationResponse = (
  response: any,
): ActiveVacation | null => {
  const vacation = vacationCandidate(response);
  if (!vacation) return null;

  return {
    id: vacation.id,
    startDate: vacation.startDate,
    endDate: vacation.endDate,
    reason: vacation.reason || '',
  };
};

export const getVacationDateKey = (
  value: string,
  userTimezone?: string,
) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const timezoneName = userTimezone || dayjs.tz.guess();
  return dayjs(value).tz(timezoneName).format('YYYY-MM-DD');
};

export const formatVacationDate = (value: string, userTimezone?: string) =>
  dayjs(getVacationDateKey(value, userTimezone)).format('DD/MM/YYYY');

export const buildVacationPayload = ({
  activeVacation,
  endDate,
  reason,
  today = dayjs().toISOString(),
  timezone: userTimezone,
}: {
  activeVacation: ActiveVacation | null;
  endDate: string;
  reason: string;
  today?: string;
  timezone?: string;
}): UserVacationPayload => ({
  startDate:
    activeVacation?.startDate ||
    getVacationStartDate(today, userTimezone),
  endDate: getVacationDateBoundary(endDate, userTimezone, 'end'),
  ...(reason.trim() ? {reason: reason.trim()} : {}),
});

export const buildEndedVacationPayload = (
  activeVacation: ActiveVacation,
  endedAt = dayjs().subtract(1, 'second').toISOString(),
): UserVacationPayload => {
  const startDate = dayjs(activeVacation.startDate);
  const requestedEndDate = dayjs(endedAt);
  const endDate = requestedEndDate.isBefore(startDate)
    ? startDate.toISOString()
    : requestedEndDate.toISOString();

  return {
    startDate: startDate.toISOString(),
    endDate,
    ...(activeVacation.reason?.trim()
      ? {reason: activeVacation.reason.trim()}
      : {}),
  };
};

const getVacationStartDate = (
  value: string,
  userTimezone: string | undefined,
) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return dayjs(value).toISOString();
  }

  return getVacationDateBoundary(value, userTimezone, 'start');
};

const getVacationDateBoundary = (
  value: string,
  userTimezone: string | undefined,
  boundary: 'start' | 'end',
) => {
  const timezoneName = userTimezone || dayjs.tz.guess();
  const parsedValue = dayjs(value);
  const dateKey = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? value
    : parsedValue.isValid()
    ? getVacationDateKey(value, timezoneName)
    : value;
  const zonedDate = dayjs.tz(dateKey, timezoneName);
  const boundedDate =
    boundary === 'start' ? zonedDate.startOf('day') : zonedDate.endOf('day');

  return boundedDate.toISOString();
};

export const isVacationEndDateValid = (endDate: string, startDate: string) =>
  Boolean(endDate) &&
  !dayjs(endDate).startOf('day').isBefore(dayjs(startDate).startOf('day'));
